import React, { useMemo } from "react";

/**
 * One plain-language answer to "can I trust what this chart is showing?"
 *
 * Replaces a cluster of developer-facing badges (Source: Historical Store,
 * Broker-aligned live, Parity 2e922360, Source: Fetch Failed) with a single
 * state a trader can read at a glance. The provenance detail is preserved on
 * hover rather than deleted, so debugging information is still one gesture
 * away.
 *
 * States are deliberately ordered worst-first: a failure must never be
 * outranked by a cosmetic label.
 */

const STATES = {
  unavailable: {
    label: "Unavailable",
    dot: "bg-red-400",
    className: "border-red-500/40 bg-red-500/15 text-red-200",
  },
  stale: {
    label: "Stale",
    dot: "bg-orange-400",
    className: "border-orange-500/40 bg-orange-500/15 text-orange-200",
  },
  delayed: {
    label: "Delayed",
    dot: "bg-amber-300",
    className: "border-amber-500/30 bg-amber-500/15 text-amber-100",
  },
  historical: {
    label: "Historical",
    dot: "bg-cyan-300",
    className: "border-cyan-500/30 bg-cyan-500/15 text-cyan-100",
  },
  simulated: {
    label: "Simulated",
    dot: "bg-fuchsia-300",
    className: "border-fuchsia-500/30 bg-fuchsia-500/15 text-fuchsia-100",
  },
  live: {
    label: "Live",
    dot: "bg-emerald-400",
    className: "border-emerald-500/30 bg-emerald-500/15 text-emerald-100",
  },
};

const STALE_AFTER_SECONDS = 120;
const DELAYED_AFTER_SECONDS = 15;

export const resolveChartTrust = ({
  source,
  isErrorSource,
  lastBarAt,
  now = Date.now(),
}) => {
  if (isErrorSource) return { key: "unavailable", ageSeconds: null };

  let ageSeconds = null;
  if (lastBarAt) {
    const iso = String(lastBarAt).endsWith("Z") ? String(lastBarAt) : `${lastBarAt}Z`;
    const parsed = new Date(iso).getTime();
    if (!Number.isNaN(parsed)) {
      ageSeconds = Math.max(0, Math.round((now - parsed) / 1000));
    }
  }

  const normalized = String(source || "").toLowerCase();
  if (normalized === "simulated") return { key: "simulated", ageSeconds };
  if (normalized.startsWith("historical")) return { key: "historical", ageSeconds };

  // Freshness outranks the label: a "live" feed that stopped updating is not live.
  if (ageSeconds !== null && ageSeconds >= STALE_AFTER_SECONDS) {
    return { key: "stale", ageSeconds };
  }
  if (ageSeconds !== null && ageSeconds >= DELAYED_AFTER_SECONDS) {
    return { key: "delayed", ageSeconds };
  }
  if (!normalized) return { key: "stale", ageSeconds };
  return { key: "live", ageSeconds };
};

const formatAge = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
};

const ChartTrustIndicator = ({
  source,
  sourceLabel,
  isErrorSource = false,
  lastBarAt,
  dataQualityLabel,
  paritySignature,
  errorDetails = [],
  className = "",
}) => {
  const { key, ageSeconds } = useMemo(
    () => resolveChartTrust({ source, isErrorSource, lastBarAt }),
    [source, isErrorSource, lastBarAt],
  );
  const state = STATES[key] || STATES.stale;
  const age = formatAge(ageSeconds);

  const tooltip = useMemo(
    () =>
      [
        `Data trust: ${state.label}`,
        sourceLabel ? `Source: ${sourceLabel}` : null,
        age ? `Last bar: ${age} ago` : null,
        dataQualityLabel ? `Quality: ${dataQualityLabel}` : null,
        paritySignature ? `Parity: ${paritySignature}` : null,
        ...(errorDetails || []).filter(Boolean),
      ]
        .filter(Boolean)
        .join("\n"),
    [state.label, sourceLabel, age, dataQualityLabel, paritySignature, errorDetails],
  );

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      className={`inline-flex h-6 items-center gap-1.5 rounded-md border px-2 text-[11px] font-medium ${state.className} ${className}`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${state.dot}`} />
      {state.label}
      {key === "delayed" && age ? <span className="opacity-75">{age}</span> : null}
      {key === "stale" && age ? <span className="opacity-75">{age}</span> : null}
    </span>
  );
};

export default ChartTrustIndicator;
